// DiaryDaySection — one day of the weekly view.
//
// Each day has its own DndContext + SortableContext. This is intentional:
// it stops dnd-kit from registering cross-day drop targets at the library
// level, so a row literally cannot be moved from Tuesday to Wednesday.
//
// The page-level DragOverlay is replaced with a per-day overlay so the
// "elevated card" follows the pointer within the day's bounds.

import { useState } from 'react';
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
  DragOverlay,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { DiaryRow } from './DiaryRow';
import { timeframeConflicts, type HolidayPeriod } from '../../../lib/holidayPeriod';
import type { BookingSummary, DiaryDay } from '../../../types/api';
import styles from './DiaryDaySection.module.css';

export interface DiaryDaySectionProps {
  day: DiaryDay;
  /** True when this day's local order differs from the server's. */
  dayChanged: boolean;
  /** Drop handler — fromIndex / toIndex are indices into day.bookings. */
  onMove: (date: string, fromIndex: number, toIndex: number) => void;
  /** Open the notes editor for the supplied booking. */
  onEditNotes: (booking: BookingSummary) => void;
  /** Diary week React Query key — threaded down so DiaryRow's Cancel /
   *  Highlight mutations (Wave B3) can invalidate the right cache. */
  diaryWeekKey?: readonly unknown[];
  /** Effective driver-holiday period on this day (null = no holiday). A
   *  booking whose timeframe clashes with it is flagged. */
  holidayPeriod?: HolidayPeriod | null;
}

function formatDayHeading(iso: string, dayOfWeek: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
  if (Number.isNaN(dt.getTime())) return `${dayOfWeek} (${iso})`;
  const formatted = dt.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  return `${dayOfWeek} ${formatted}`;
}

export function DiaryDaySection({
  day,
  dayChanged,
  onMove,
  onEditNotes,
  diaryWeekKey,
  holidayPeriod = null,
}: DiaryDaySectionProps): JSX.Element {
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleDragStart(event: DragStartEvent) {
    setActiveId(String(event.active.id));
  }

  function handleDragEnd(event: DragEndEvent) {
    setActiveId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const fromIndex = day.bookings.findIndex((b) => b.enc_id === active.id);
    const toIndex = day.bookings.findIndex((b) => b.enc_id === over.id);
    if (fromIndex === -1 || toIndex === -1) return;
    onMove(day.date, fromIndex, toIndex);
  }

  function handleDragCancel() {
    setActiveId(null);
  }

  const items = day.bookings.map((b) => b.enc_id);
  const activeBooking = activeId
    ? day.bookings.find((b) => b.enc_id === activeId) ?? null
    : null;

  return (
    <section className={styles.section} role="rowgroup" aria-label={`${day.day_of_week} bookings`}>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <span className={styles.dayTitle}>
            {formatDayHeading(day.date, day.day_of_week)}
          </span>
          <span className={styles.dayCount}>
            {day.bookings.length}
            {day.bookings.length === 1 ? ' booking' : ' bookings'}
          </span>
        </div>
        {dayChanged ? (
          <span className={styles.dayChanged} aria-live="polite">
            Order changed (unsaved)
          </span>
        ) : null}
      </header>

      {/* Day notes were originally rendered inline here; they are now
          handled by <DayNotesSection> at the page level (CP2 correction
          adds full CRUD on top, with its own card layout above the
          bookings table). */}

      {day.bookings.length === 0 ? (
        <div className={styles.empty}>No bookings for this day</div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <SortableContext items={items} strategy={verticalListSortingStrategy}>
            <div className={styles.body}>
              {day.bookings.map((b) => (
                <DiaryRow
                  key={b.enc_id}
                  booking={b}
                  onEditNotes={onEditNotes}
                  diaryWeekKey={diaryWeekKey}
                  holidayConflict={timeframeConflicts(b.timeframe, holidayPeriod)}
                />
              ))}
            </div>
          </SortableContext>
          <DragOverlay>
            {activeBooking ? (
              <DiaryRow booking={activeBooking} onEditNotes={() => undefined} asOverlay />
            ) : null}
          </DragOverlay>
        </DndContext>
      )}
    </section>
  );
}
